import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { FilingTransmission } from '@/types/filings';

interface FilingTransmissionStatusProps {
  transmissionId: number;
  onComplete?: () => void;
}

export const FilingTransmissionStatus: React.FC<FilingTransmissionStatusProps> = ({
  transmissionId,
  onComplete
}) => {
  const [status, setStatus] = useState<FilingTransmission | null>(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const checkStatus = async () => {
      try {
        const response = await ApiService.request<FilingTransmission>(
          EndpointKeys.GET_TRANSMISSION_STATUS,
          {
            urlParams: { id: transmissionId }
          }
        );
        
        setStatus(response);

        if (response.status === 'COMPLETED' || response.status === 'FAILED') {
          setPolling(false);
          if (intervalId) clearInterval(intervalId);
          if (response.status === 'COMPLETED' && onComplete) {
            onComplete();
          }
        }
      } catch (error) {
        console.error('Failed to fetch transmission status:', error);
      }
    };

    if (polling) {
      checkStatus();
      intervalId = setInterval(checkStatus, 10000); // Poll every 10 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [transmissionId, polling, onComplete]);

  if (!status) {
    return <div>Loading...</div>;
  }

  const getStatusBadge = () => {
    switch (status.status) {
      case 'SUBMITTED':
        return (
          <Badge variant="default" className="bg-blue-500">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Submitted - Polling HMRC
          </Badge>
        );
      case 'COMPLETED':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="mr-1 h-3 w-3" />
            Successfully Submitted
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Submission Failed
          </Badge>
        );
      default:
        return <Badge>{status.status}</Badge>;
    }
  };

  return (
    <div className="space-y-2" data-testid="transmission-status">
      <div className="flex items-center space-x-2">
        {getStatusBadge()}
        {status.isTest && <Badge variant="outline">Test</Badge>}
      </div>
      
      {status.correlationId && (
        <p className="text-sm text-gray-600">
          Correlation ID: {status.correlationId}
        </p>
      )}
      
      {status.errorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{status.errorMessage}</AlertDescription>
        </Alert>
      )}
      
      {status.irMarkReceipt && status.status === 'COMPLETED' && (
        <div className="mt-2 p-2 bg-green-50 rounded">
          <p className="text-sm font-medium">HMRC Receipt Received</p>
          <p className="text-xs text-gray-600">
            Accepted at: {new Date(status.completedAt!).toLocaleString('en-GB')}
          </p>
        </div>
      )}
    </div>
  );
}; 